import React, { useEffect, useMemo, useState } from 'react';
import {
  adminListReports,
  adminReportCounts,
  adminUpdateReportStatus,
} from '../api/socialActions';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { useToast } from '../hooks/use-toast';
import { AlertTriangle, Eye, Copy, RefreshCw, Archive, CheckCircle2, Clock, Inbox, Pencil } from 'lucide-react';
import PostPreviewModal from './PostPreviewModal';

const STATUS_META = {
  nuova: { label: 'Nuova', class: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  in_revisione: { label: 'In revisione', class: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  risolta: { label: 'Risolta', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  archiviata: { label: 'Archiviata', class: 'bg-neutral-500/15 text-neutral-400 border-neutral-500/30' },
};

const REASON_LABEL = {
  inappropriate: 'Inappropriato',
  sexual_adult: 'Sessuale / Adulti',
  minors_grooming: 'Minori / Grooming',
  spam: 'Spam',
  rules_violation: 'Violazione regole',
  offensive: 'Offensivo',
  copyright: 'Copyright',
  other: 'Altro',
};

const SLA_MS = 24 * 60 * 60 * 1000;

const fmtHm = (ms) => {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
};

// Phase F9.3 — visual 24h operational SLA (no automated actions).
const SlaBadge = ({ report }) => {
  const created = new Date(report.created_at).getTime();
  const closed = report.status === 'risolta' || report.status === 'archiviata';
  if (closed) {
    if (!report.resolved_at) return null;
    const took = new Date(report.resolved_at).getTime() - created;
    const within = took <= SLA_MS;
    return (
      <span
        className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${
          within
            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
            : 'border-red-500/30 bg-red-500/10 text-red-400'
        }`}
        data-testid={`report-sla-${report.id}`}
      >
        Gestita in {fmtHm(Math.max(took, 0))}{within ? '' : ' · oltre 24h'}
      </span>
    );
  }
  const remaining = created + SLA_MS - Date.now();
  if (remaining <= 0) {
    return (
      <span
        className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-400"
        data-testid={`report-sla-${report.id}`}
      >
        In ritardo
      </span>
    );
  }
  const warning = remaining < 6 * 3600000;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${
        warning
          ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
          : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
      }`}
      data-testid={`report-sla-${report.id}`}
    >
      Entro {fmtHm(remaining)}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.nuova;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.class}`}
      data-testid={`report-status-${status}`}
    >
      {meta.label}
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg border border-border bg-card p-3">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <p className="mt-1 text-2xl font-semibold">{value}</p>
  </div>
);

const AdminReportsPanel = () => {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ nuova: 0, in_revisione: 0, risolta: 0, archiviata: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  // Phase 51 · Preview modal state. Local so filters/scroll survive close.
  const [previewId, setPreviewId] = useState(null);
  // Phase F9.3 — "azione intrapresa" edit dialog: { id, text } | null.
  const [actionEdit, setActionEdit] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rows, c] = await Promise.all([
        adminListReports(statusFilter === 'all' ? undefined : statusFilter),
        adminReportCounts(),
      ]);
      setReports(rows || []);
      setCounts(c || counts);
    } catch (err) {
      toast({ title: 'Impossibile caricare le segnalazioni', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copiato', description: text });
    } catch {
      toast({ title: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  const changeStatus = async (id, next) => {
    try {
      await adminUpdateReportStatus(id, next);
      toast({ title: 'Stato aggiornato' });
      await load();
    } catch (err) {
      toast({
        title: 'Aggiornamento fallito',
        description: err?.response?.data?.detail || 'Riprova.',
        variant: 'destructive',
      });
    }
  };

  const saveAction = async () => {
    if (!actionEdit) return;
    setActionBusy(true);
    try {
      await adminUpdateReportStatus(actionEdit.id, null, actionEdit.text);
      toast({ title: 'Azione salvata' });
      setActionEdit(null);
      await load();
    } catch (err) {
      toast({
        title: 'Salvataggio fallito',
        description: err?.response?.data?.detail || 'Riprova.',
        variant: 'destructive',
      });
    } finally {
      setActionBusy(false);
    }
  };

  const rows = useMemo(() => reports, [reports]);
  // Overdue among loaded open rows (visual indicator only).
  const overdueCount = useMemo(
    () => rows.filter((r) => (r.status === 'nuova' || r.status === 'in_revisione')
      && Date.now() - new Date(r.created_at).getTime() > SLA_MS).length,
    [rows],
  );

  return (
    <div className="space-y-4" data-testid="admin-reports-panel">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 gold-text" />
            <h2 className="text-lg font-semibold">Segnalazioni</h2>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid="reports-sla-note">
            Obiettivo operativo: gestire ogni segnalazione entro 24 ore dalla ricezione.
            {overdueCount > 0 && (
              <span className="ml-1 font-semibold text-red-400" data-testid="reports-overdue-count">
                {overdueCount} in ritardo in questa vista.
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="reports-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="nuova">Nuove</SelectItem>
              <SelectItem value="in_revisione">In revisione</SelectItem>
              <SelectItem value="risolta">Risolte</SelectItem>
              <SelectItem value="archiviata">Archiviate</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} data-testid="reports-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Totale" value={counts.total || 0} icon={Inbox} />
        <StatCard label="Nuove" value={counts.nuova || 0} icon={AlertTriangle} />
        <StatCard label="In revisione" value={counts.in_revisione || 0} icon={Clock} />
        <StatCard label="Risolte" value={counts.risolta || 0} icon={CheckCircle2} />
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">ID</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Autore</TableHead>
              <TableHead>Segnalatore</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead>Ricevuta · 24h</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-14 text-center">
                  <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-border border-t-amber-400" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-14 text-center text-sm text-muted-foreground">
                  Nessuna segnalazione.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                  <TableCell className="font-mono text-[11px]">
                    <button
                      onClick={() => copy(r.id)}
                      className="inline-flex items-center gap-1 hover:underline"
                      data-testid={`report-copy-id-${r.id}`}
                    >
                      {r.id.slice(0, 8)}… <Copy className="h-3 w-3" />
                    </button>
                  </TableCell>
                  <TableCell>
                    {r.target_type === 'user' ? (
                      /* Phase F4 — user reported from the chat/vetrina */
                      <span
                        className="inline-flex items-center rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] font-semibold text-sky-400"
                        data-testid={`report-user-target-${r.id}`}
                      >
                        Utente
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{r.post_short_id}</span>
                        <button
                          onClick={() => copy(r.post_short_id)}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`report-copy-post-id-${r.id}`}
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {r.notes && (
                      <p className="mt-0.5 line-clamp-2 max-w-[220px] text-[11px] text-muted-foreground">
                        {r.notes}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">@{r.author_username}</TableCell>
                  <TableCell className="text-xs">@{r.reporter_username}</TableCell>
                  <TableCell className={`text-xs ${r.reason === 'minors_grooming' ? 'font-bold text-red-400' : ''}`}>
                    {REASON_LABEL[r.reason] || r.reason}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-[11px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString('it-IT')}{' '}
                    {new Date(r.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    <div className="mt-1">
                      <SlaBadge report={r} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusPill status={r.status} />
                    {r.resolved_at && (
                      <p className="mt-0.5 text-[10px] text-muted-foreground" data-testid={`report-resolved-at-${r.id}`}>
                        Chiusa: {new Date(r.resolved_at).toLocaleDateString('it-IT')}{' '}
                        {new Date(r.resolved_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                    {r.action_taken && (
                      <p className="mt-0.5 line-clamp-1 max-w-[160px] text-[10px] text-muted-foreground" data-testid={`report-action-taken-${r.id}`}>
                        Azione: {r.action_taken}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {r.target_type !== 'user' && (
                        <button
                          type="button"
                          onClick={() => setPreviewId(r.post_id)}
                          className="inline-flex h-8 items-center justify-center rounded-md border border-border px-2 hover:bg-secondary"
                          title="Anteprima post"
                          data-testid={`report-open-post-${r.id}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setActionEdit({ id: r.id, text: r.action_taken || '' })}
                        className="inline-flex h-8 items-center justify-center rounded-md border border-border px-2 hover:bg-secondary"
                        title="Azione intrapresa"
                        data-testid={`report-edit-action-${r.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <Select
                        value={r.status}
                        onValueChange={(v) => changeStatus(r.id, v)}
                      >
                        <SelectTrigger className="h-8 w-32" data-testid={`report-status-select-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nuova">Nuova</SelectItem>
                          <SelectItem value="in_revisione">In revisione</SelectItem>
                          <SelectItem value="risolta">Risolta</SelectItem>
                          <SelectItem value="archiviata">Archiviata</SelectItem>
                        </SelectContent>
                      </Select>
                      {r.status !== 'archiviata' && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => changeStatus(r.id, 'archiviata')}
                          title="Archivia"
                          data-testid={`report-archive-${r.id}`}
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Phase F9.3 — free-text "azione intrapresa" (human moderation log) */}
      <Dialog open={!!actionEdit} onOpenChange={(o) => !o && setActionEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="report-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4 gold-text" /> Azione intrapresa
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={actionEdit?.text || ''}
            onChange={(e) => setActionEdit((s) => ({ ...s, text: e.target.value }))}
            rows={3}
            maxLength={500}
            placeholder="Es. Utente avvisato, contenuto rimosso, nessuna azione necessaria…"
            data-testid="report-action-input"
          />
          <Button onClick={saveAction} disabled={actionBusy} className="w-full" data-testid="report-action-save">
            {actionBusy ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Phase 51 · Post preview modal — keeps filters/scroll intact */}
      <PostPreviewModal
        open={!!previewId}
        onOpenChange={(o) => !o && setPreviewId(null)}
        postId={previewId}
      />
    </div>
  );
};

export default AdminReportsPanel;
